import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import update from 'immutability-helper';
import moment from 'moment';
import { formatDecimal } from "@/lib/number_format";
import { pluralWord } from "@/lib/plural";

// Will recursively search through the children for React elements to add a method to
export function addMethodToChildren(
  children,
  propName,
  func,
  filterFunc = () => true
) {
  if (
    children === null ||
    children === undefined
  ) {
    return children;
  }

  if (
    React.isValidElement(
      children
    ) &&
    children.type ===
    FontAwesomeIcon
  ) {
    return children;
  }

  return React.Children.map(
    children,
    (child) => {
      if (
        !React.isValidElement(
          child
        )
      ) {
        return child;
      }

      if (
        child.type ===
        FontAwesomeIcon
      ) {
        return child;
      }

      if (
        !filterFunc(child)
      ) {
        return false;
      }

      const newProps =
        typeof child.type ===
          'function'
          ? {
            [propName]:
              func,
          }
          : {};

      const newChildren =
        child.props &&
          child.props.children !==
          undefined
          ? addMethodToChildren(
            child.props.children,
            propName,
            func
          )
          : undefined;

      return React.cloneElement(
        child,
        newProps,
        newChildren
      );
    }
  );
}

export const uuid =
  (function () {
    function s4() {
      return Math
        .floor(
          (
            1 +
            Math.random()
          ) *
          0x10000
        )
        .toString(16)
        .substring(1);
    }

    return function () {
      return (
        s4() +
        s4() +
        '-' +
        s4() +
        '-' +
        s4() +
        '-' +
        s4() +
        '-' +
        s4() +
        s4() +
        s4()
      );
    };
  })();

export const objectify = (
  collection,
  label = 'id'
) => {
  return collection.reduce(
    (
      result,
      element
    ) => {
      result[
        element[label]
      ] = element;

      return result;
    },
    {}
  );
};

export const sameObjects = (
  obj1,
  obj2
) => {
  return (
    JSON.stringify(obj1) ===
    JSON.stringify(obj2)
  );
};

export const removeEmptyKeys =
  function (
    obj,
    isTruthy
  ) {
    const truthyOnly =
      isTruthy || false;

    const newObj = {};

    let key;

    for (key in obj) {
      const value =
        obj[key];

      if (
        !(
          value ===
          undefined ||
          value === null ||
          value.length === 0 ||
          (
            truthyOnly &&
            !value
          )
        )
      ) {
        newObj[key] =
          value;
      }
    }

    return newObj;
  };

export const deparam =
  function (
    params,
    coerce
  ) {
    const decode =
      decodeURIComponent;

    const obj = {};

    const coerceTypes = {
      true: true,
      false: false,
      null: null,
    };

    params
      .replace(
        /\+/g,
        ' '
      )
      .split('&')
      .forEach((v) => {
        const param =
          v.split('=');

        let key =
          decode(
            param[0]
          );

        let val;
        let cur = obj;
        let i = 0;

        let keys =
          key.split('][');

        let keysLast =
          keys.length - 1;

        if (
          /\[/.test(
            keys[0]
          ) &&
          /\]$/.test(
            keys[
            keysLast
            ]
          )
        ) {
          keys[
            keysLast
          ] =
            keys[
              keysLast
            ].replace(
              /\]$/,
              ''
            );

          keys =
            keys
              .shift()
              .split('[')
              .concat(keys);

          keysLast =
            keys.length - 1;
        } else {
          keysLast = 0;
        }

        if (
          param.length === 2
        ) {
          val =
            decode(
              param[1]
            );

          if (coerce) {
            val =
              val &&
                !isNaN(val)
                ? +val
                : val ===
                  'undefined'
                  ? undefined
                  : coerceTypes[
                    val
                  ] !==
                    undefined
                    ? coerceTypes[
                    val
                    ]
                    : val;
          }

          if (keysLast) {
            for (
              ;
              i <= keysLast;
              i++
            ) {
              key =
                keys[i] === ''
                  ? cur.length
                  : keys[i];

              cur =
                cur[key] =
                i <
                  keysLast
                  ? cur[
                  key
                  ] ||
                  (
                    keys[
                      i + 1
                    ] &&
                      isNaN(
                        keys[
                        i +
                        1
                        ]
                      )
                      ? {}
                      : []
                  )
                  : val;
            }
          } else if (
            Array.isArray(
              obj[key]
            )
          ) {
            obj[key].push(
              val
            );
          } else if (
            obj[key] !==
            undefined
          ) {
            obj[key] = [
              obj[key],
              val,
            ];
          } else {
            obj[key] =
              val;
          }
        } else if (key) {
          obj[key] =
            coerce
              ? undefined
              : '';
        }
      });

    return obj;
  };

export const isEmptyObject =
  function (obj) {
    if (
      obj === null ||
      obj === undefined
    ) {
      return true;
    }

    for (const _key in obj) {
      return false;
    }

    return true;
  };

export const removeEmptyElementsFromArray =
  function (array) {
    return array.filter(
      (el) =>
        !(
          el ===
          undefined ||
          el === null ||
          el === ''
        )
    );
  };

export const getDurationInMonths =
  function (
    startDate,
    endDate
  ) {
    const start =
      moment(startDate);

    const end =
      moment(endDate);

    return moment
      .duration(
        end.diff(start)
      )
      .asMonths();
  };

export const getFormattedDurationInMonths =
  function (
    startDate,
    endDate
  ) {
    const months =
      getDurationInMonths(
        startDate,
        endDate
      );

    const formattedMonths =
      formatDecimal(months);

    return `${formattedMonths} ${pluralWord(
      formattedMonths,
      "month"
    )}`;
  };

export const formattedNumber =
  function (
    number,
    style = 'currency',
    currency = 'USD',
    maximumFractionDigits = 0
  ) {
    if (number) {
      return new Intl
        .NumberFormat(
          'en-US',
          {
            style,
            currency,
            maximumFractionDigits,
          }
        )
        .format(number);
    }

    return 0;
  };

export const twoDecimals =
  function (value) {
    return (
      Math.round(
        value * 100
      ) / 100
    );
  };

export const debounce =
  function (
    key,
    callback,
    delay = 1000
  ) {
    debounce.types =
      debounce.types ||
      new Map();

    clearTimeout(
      debounce.types.get(
        key
      )
    );

    debounce.types.set(
      key,
      setTimeout(
        () => {
          callback();

          debounce.types.delete(
            key
          );
        },
        delay
      )
    );
  };
